<template>
  <div v-if="visible" class="modal">
    <transition
      name="fade"
      @before-enter="beforeModalTransitionEnter"
      @after-enter="afterModalTransitionEnter"
      @before-leave="beforeModalTransitionLeave"
      @after-leave="afterModalTransitionLeave"
    >
      <div
        class="modal__wrapper"
        v-if="visibility.modal"
        ref="modal"
        role="dialog"
        :aria-expanded="visibility.modal.toString()"
        aria-modal="true"
      >
        <div
          v-if="visibility.overlay"
          @click.stop="onOverlayClick"
          class="modal__wrapper__overlay"
          :aria-expanded="visibility.overlay.toString()"
        ></div>
        <div class="modal__wrapper__box" :style="{ width: `${width}px` }">
          <header class="modal__wrapper__box__header">
            <ui-button
              class="modal__wrapper__box__header__close"
              @click="close"
              button="secondary"
              :label="$t('commons.buttons.close')"
              :icon-only="true"
              icon="close"
            />
            <div class="modal__wrapper__box__header__title">
              {{ title }}
            </div>
          </header>
          <div class="modal__wrapper__box__container">
            <slot name="container"></slot>
          </div>
          <footer class="modal__wrapper__box__footer">
            <slot name="actions">
              <ui-button
                class="modal__wrapper__box__footer__close"
                :label="$t('commons.buttons.close')"
                button="primary"
                @click.prevent="close"
                v-if="hasClose"
              />
              <ui-button
                class="modal__wrapper__box__footer__apply"
                :label="$t('commons.buttons.apply')"
                button="cta"
                variant="gtr"
                @click.prevent="apply"
                v-if="hasApply"
              />
            </slot>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import { blurActiveElement } from './utils'
import UiButton from '@/components/UI/Button.vue'

const TransitionState = {
  Enter: 'enter',
  Entering: 'entering',
  Leave: 'leave',
  Leaving: 'leaving',
}

export default {
  name: 'Modal',
  components: {
    UiButton,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    hasClose: {
      type: Boolean,
      default: true,
    },
    hasApply: {
      type: Boolean,
      default: false,
    },
    width: {
      type: [Number, String],
      default: 600,
    },
    clickToClose: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visible: false,
      visibility: {
        modal: false,
        overlay: false,
      },
      overlayTransitionState: null,
      modalTransitionState: null,
    }
  },
  /**
   * Sets global listeners
   */
  beforeMount() {
    this.$modal.subscription.$on('toggle', this.onToggle)

    if (this.clickToClose) {
      window.addEventListener('keyup', this.onEscapeKeyUp)
    }
  },
  /**
   * Removes global listeners
   */
  beforeDestroy() {
    this.$modal.subscription.$off('toggle', this.onToggle)

    if (this.clickToClose) {
      window.removeEventListener('keyup', this.onEscapeKeyUp)
    }
  },
  watch: {
    isComponentReadyToBeDestroyed(isReady) {
      if (isReady) {
        this.visible = false
      }
    },
  },
  computed: {
    isComponentReadyToBeDestroyed() {
      return this.modalTransitionState === TransitionState.Leave
    },
  },
  methods: {
    startTransitionEnter() {
      this.visibility.overlay = true
      this.visibility.modal = true
    },

    startTransitionLeave() {
      this.visibility.overlay = false
      this.visibility.modal = false
    },

    beforeModalTransitionEnter() {
      this.modalTransitionState = TransitionState.Entering
    },

    beforeModalTransitionLeave() {
      this.modalTransitionState = TransitionState.Leaving
    },

    afterModalTransitionEnter() {
      this.modalTransitionState = TransitionState.Enter
      this.$emit('opened', event)
    },

    afterModalTransitionLeave() {
      this.modalTransitionState = TransitionState.Leave
      this.$emit('closed', event)
    },

    onToggle(name, state, params) {
      if (this.name === name) {
        const nextState = typeof state === 'undefined' ? !this.visible : state

        this.toggle(nextState, params)
      }
    },

    onEscapeKeyUp(event) {
      if (event.which === 27 && this.visible) {
        this.$modal.hide(this.name)
      }
    },
    open() {
      this.$emit('before-open', event)
      /**
       * Need to unfocus previously focused element, otherwise
       * all keypress events (ESC press, for example) will trigger on that element.
       */
      blurActiveElement()

      this.visible = true
      /* Making sure that entering tranition uses "enter" sequance instead of "appear" */
      this.$nextTick(() => {
        this.startTransitionEnter()
      })
    },

    close() {
      this.$emit('before-close', event)
      this.startTransitionLeave()
    },
    apply() {
      this.$emit('apply')
      this.$modal.hide(this.name)
    },

    toggle(isOpening, params) {
      const { visible } = this

      if (visible === isOpening) {
        return
      }

      if (isOpening) {
        this.open(params)
      } else {
        this.close(params)
      }
    },

    /**
     * Event handler that is triggered when background overlay is clicked
     */
    onOverlayClick() {
      if (this.clickToClose) {
        this.toggle(false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.modal {
  &__wrapper {
    @include wrapper($justify: center, $zIndex: 100, $align: center);

    &__overlay {
      @include overlay;
    }

    &__box {
      @include box(101);

      &__header {
        @include header;

        &__close {
          @include close;
        }

        &__title {
          @include title;
        }
      }

      &__container {
        @include container;
      }

      &__footer {
        @include footer;
      }
    }
  }
}
</style>
